import React from "react";
import { useTranslation } from "react-i18next";

/**
 * "From RSVP to guest list, without starting over." — the section the whole
 * positioning rests on, plus the four beats a reply actually passes through.
 *
 * Rendered with the site's existing `wps-flow` treatment (introduced by
 * `PhotoSharingQr` for Scan → Join → Share) rather than a new layout: it is the
 * same kind of thing — a real ordered sequence where each step depends on the
 * one before it — which is why it is an <ol> and why the arrows are decorative
 * (`aria-hidden`) rather than content.
 *
 * The sequence is not a metaphor. Each arrow is a real derivation in the
 * implementation:
 *   - RSVP → guest list: `POST /api/events/:eventId/rsvp/:code` writes the
 *     AttendanceRecord that the guest list reads back
 *     (api/routes/rsvp.routes.mjs).
 *   - guest list → headcount: `attendingHeadcount` is aggregated from those
 *     responses across approved guests and approved party members
 *     (api/services/guestStats.mjs, served by `/guests/stats`).
 *   - headcount → seating: the seating pool is NOT stored — it is computed
 *     live from the members with an "attending" response to a reception
 *     section (api/services/seatingPool.mjs).
 * Nothing here may be reworded into "syncs" or "updates automatically": these
 * are derivations on read, and the couple is the only one who ever writes.
 */
const FLOW_KEYS = ["rsvp", "guestList", "headcount", "seating"];

const RsvpFlow = () => {
  const { t } = useTranslation();

  return (
    <section className="section topless wps-section">
      <div className="container">
        <div className="wps-editorial wps-editorial-solo">
          <div className="wps-editorial-text">
            <h2 className="heading-50">{t("weddingRsvp.guestList.heading")}</h2>
            <p className="body-20">{t("weddingRsvp.guestList.body1")}</p>
            <p className="body-20">{t("weddingRsvp.guestList.body2")}</p>
            <p className="body-20">{t("weddingRsvp.guestList.body3")}</p>
            <p className="wps-note">{t("weddingRsvp.guestList.note")}</p>
          </div>
        </div>

        <div className="wps-flow" aria-labelledby="wrs-flow-label">
          <p id="wrs-flow-label" className="wps-eyebrow wps-flow-label">
            {t("weddingRsvp.guestList.flowLabel")}
          </p>
          <ol className="wps-flow-list">
            {FLOW_KEYS.map((key, index) => (
              <li key={key} className="wps-flow-step">
                <span className="wps-flow-step-txt">
                  {t(`weddingRsvp.guestList.flow.${key}`)}
                </span>
                {index < FLOW_KEYS.length - 1 ? (
                  <span className="wps-flow-arrow" aria-hidden="true">
                    →
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default RsvpFlow;
